import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { sanitizeUrl } from '@trello/safe-urls';
import { getCsrfRequestPayload } from '@trello/session-cookie/csrf';

// import { client } from '../client';
import { safeFetch } from '../fetch';
import type {
  Automation,
  Automation_Type,
  AutomationLibrary,
  MemberAutomationsDataArgs,
  MutationDeleteAutomationArgs,
  MutationEnableAllLibraryAutomationsArgs,
  MutationEnableAutomationArgs,
  MutationImportAutomationLibraryArgs,
} from '../generated';
import { isQueryInfo } from '../isQueryInfo';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type { TrelloRestResolver } from '../types';

export type ButlerResponseAutomation = {
  b: string[];
  cmd: string;
  id: string;
  lib?: string;
  t: number;
  type: string;
  uid: string;
};

export type ButlerResponseLibrary = {
  date: string;
  link_id: string;
  org_id: string;
  title: string;
  user_id: string;
  username: string;
};

const mapAutomationType = (type: string): Automation_Type => {
  switch (type) {
    case 'rule':
      return 'rule';
    case 'schedule':
      return 'schedule';
    case 'on-date':
      return 'onDate';
    default:
      throw new Error(`Unknown automation type: ${type}`);
  }
};

const transformAutomationsFromButler = (
  commands: ButlerResponseAutomation[],
) => {
  return commands.map((command) => {
    return {
      __typename: 'Automation' as const,
      automationNLString: command.cmd,
      enabledBoards: command.b,
      id: command.id,
      idMember: command.uid,
      lastModified: Number(command.t),
      libraryName: command.lib,
      type: mapAutomationType(command.type),
    };
  });
};

const transformLibrariesFromButler = (libraries: ButlerResponseLibrary[]) =>
  libraries.map((library) => ({
    __typename: 'AutomationLibrary' as const,
    containerId: library.org_id,
    memberId: library.user_id,
    shareId: library.link_id,
    name: library.title,
  }));

export const memberAutomationsResolver: TrelloRestResolver<
  MemberAutomationsDataArgs
> = async (_member, args, context, info) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { boardId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands?b=${{
    value: boardId,
    type: 'boardId',
  }}`;

  const result = await safeFetch(apiUrl, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
  });

  if (result.ok) {
    const { response, success } = await result.json();

    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];
      const libraries: AutomationLibrary[] = response?.libs
        ? transformLibrariesFromButler(response?.libs)
        : [];
      const model = {
        automations,
        libraries,
      };

      return prepareDataForApolloCache(model, rootNode, 'Member');
    } else {
      sendNetworkErrorEvent({
        url: apiUrl,
        response: await result.clone().text(),
        status: result.status,
        operationName: context.operationName,
      });
      throw await parseNetworkError(result);
    }
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await result.clone().text(),
      status: result.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(result);
  }
};

export const enableAutomation: TrelloRestResolver<
  MutationEnableAutomationArgs
> = async (_object, args, context, info) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { automationId, boardId, enable } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands/${{
    value: automationId,
    type: 'automationId',
  }}`;

  const result = await safeFetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      op: 'enable',
      params: {
        board: { id: boardId },
        enabled: enable,
      },
      ...getCsrfRequestPayload(),
    }),
  });

  if (result.ok) {
    const { response, success } = await result.json();

    if (success) {
      const model: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];

      return prepareDataForApolloCache(model, rootNode);
    } else {
      sendNetworkErrorEvent({
        url: apiUrl,
        response: await result.clone().text(),
        status: result.status,
        operationName: context.operationName,
      });
      throw await parseNetworkError(result);
    }
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await result.clone().text(),
      status: result.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(result);
  }
};

export const deleteAutomation: TrelloRestResolver<
  MutationDeleteAutomationArgs
> = async (_object, args, context, info) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { automationId, boardId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-commands/${{
    value: automationId,
    type: 'automationId',
  }}`;

  const result = await safeFetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      op: 'delete',
      params: {
        board: { id: boardId },
      },
      ...getCsrfRequestPayload(),
    }),
  });

  if (result.ok) {
    const { success } = await result.json();

    if (success) {
      return prepareDataForApolloCache({ id: automationId }, rootNode);
    } else {
      sendNetworkErrorEvent({
        url: apiUrl,
        response: await result.clone().text(),
        status: result.status,
        operationName: context.operationName,
      });
      throw await parseNetworkError(result);
    }
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await result.clone().text(),
      status: result.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(result);
  }
};

export const enableAllLibraryAutomations: TrelloRestResolver<
  MutationEnableAllLibraryAutomationsArgs
> = async (_object, args, context, info) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const {
    boardId,
    boardName,
    boardUrl,
    containerId,
    enable,
    libraryName,
    memberId,
  } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-enable`;

  const result = await safeFetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      board: {
        id: boardId,
        name: boardName,
        url: boardUrl,
      },
      enable,
      idOrganization: containerId,
      title: libraryName,
      ...getCsrfRequestPayload(),
    }),
  });

  if (result.ok) {
    const { response, success } = await result.json();

    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];

      console.warn(memberId);
      // client.cache.modify({
      //   id: client.cache.identify({ __typename: 'Member', id: memberId }),
      //   fields: {
      //     automations(existingRefs = [], { readField, toReference }) {
      //       const automationRefs = automations.map(({ id }) =>
      //         toReference({
      //           __typename: 'Automation',
      //           id,
      //         }),
      //       );

      //       return automationRefs;
      //     },
      //   },
      // });

      return prepareDataForApolloCache(automations, rootNode);
    } else {
      sendNetworkErrorEvent({
        url: apiUrl,
        response: await result.clone().text(),
        status: result.status,
        operationName: context.operationName,
      });
      throw await parseNetworkError(result);
    }
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await result.clone().text(),
      status: result.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(result);
  }
};

export const importAutomationLibrary: TrelloRestResolver<
  MutationImportAutomationLibraryArgs
> = async (_object, args, context, info) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { boardId, workspaceId, shareId } = args;

  const apiUrl = sanitizeUrl`/proxy/butler/powerup-library-import/${{
    value: shareId,
    type: 'automationShareId',
  }}`;
  const result = await safeFetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      board: { id: boardId },
      idOrganization: workspaceId,
      ...getCsrfRequestPayload(),
    }),
  });

  if (result.ok) {
    const json = await result.json();
    const { response, success } = json;
    if (success) {
      const automations: Automation[] = response?.commands
        ? transformAutomationsFromButler(response?.commands)
        : [];

      return prepareDataForApolloCache(automations, rootNode);
    } else {
      sendNetworkErrorEvent({
        url: apiUrl,
        response: await result.clone().text(),
        status: result.status,
        operationName: context.operationName,
      });
      throw await parseNetworkError(result);
    }
  } else {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await result.clone().text(),
      status: result.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(result);
  }
};
